import { useIntl } from "react-intl";
import { toast } from "react-toastify";

import { useRevokeDeveloperApiToken } from "api/queries/user/useRevokeDeveloperApiToken";
import { ReactComponent as TrashIcon } from "assets/images/iconsV2/trash-01.svg";
import Button from "components/v2/Button/Button";
import IconCircle from "components/v2/IconCircle/IconCircle";
import Modal from "components/v2/Modal/Modal";

interface DeveloperApiToken {
  id: number;
  name: string;
}

interface RevokeDeveloperApiTokenModalProps {
  token: DeveloperApiToken;
  open: boolean;
  onClose: () => void;
}

export default function RevokeDeveloperApiTokenModal({ token, open, onClose }: RevokeDeveloperApiTokenModalProps) {
  const intl = useIntl();
  const { mutate: revokeToken, isLoading } = useRevokeDeveloperApiToken();

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  const handleConfirm = () => {
    revokeToken(token.id, {
      onSuccess: () => {
        toast(
          intl.formatMessage(
            {
              id: "settings.developerApiTokens.revokeSuccess",
              defaultMessage: '"{name}" has been revoked.',
            },
            { name: token.name },
          ),
          { type: "success" },
        );
        onClose();
      },
      onError: () => {
        toast(
          intl.formatMessage({
            id: "settings.developerApiTokens.revokeError",
            defaultMessage: "Failed to revoke the token. Please try again.",
          }),
          { type: "error" },
        );
      },
    });
  };

  return (
    <Modal
      show={open}
      onHide={handleClose}
      size="lg"
      closeWithBackdropClick={!isLoading}
      closeWithEscape={!isLoading}
    >
      <Modal.Header>
        <div className="d-flex flex-column">
          <div className="align-self-start developer-api-token-revoke-icon">
            <IconCircle icon={TrashIcon} size="lg" color="error" />
          </div>
          <div className="mt-xl text-primary text-xl font-semibold">
            {intl.formatMessage({
              id: "settings.developerApiTokens.revokeDialog.title",
              defaultMessage: "Revoke token",
            })}
          </div>
          <div className="mt-xl text-tertiary text-sm">
            {intl.formatMessage(
              {
                id: "settings.developerApiTokens.revokeDialog.description",
                defaultMessage: 'Are you sure you want to revoke "{name}"?',
              },
              { name: token.name },
            )}
          </div>
          <div className="mt-md text-tertiary text-sm">
            {intl.formatMessage({
              id: "settings.developerApiTokens.revokeDialog.warning",
              defaultMessage:
                "Once revoked, any applications using this API key will no longer be authorized to access the API. This action cannot be undone.",
            })}
          </div>
        </div>
      </Modal.Header>
      <Modal.Footer>
        <div className="d-flex align-items-center justify-content-end">
          <div className="mr-lg">
            <Button
              label={intl.formatMessage({ id: "general.cancel", defaultMessage: "Cancel" })}
              onClick={handleClose}
              variant="secondary-gray"
              disabled={isLoading}
            />
          </div>
          <Button
            label={intl.formatMessage({
              id: "settings.developerApiTokens.revoke",
              defaultMessage: "Revoke",
            })}
            onClick={handleConfirm}
            variant="error"
            disabled={isLoading}
          />
        </div>
      </Modal.Footer>
    </Modal>
  );
}
